<template>
  <div class="feedback-form">
    <form
      v-if="!success"
      :id="formId"
      v-validity="validity"
      data-test-id="feedback-form-element"
      novalidate
      @submit.prevent="send"
    >
      <component
        :is="titleAs"
        v-if="title"
        class="feedback-form__title"
        data-test-id="feedback-form-title"
      >
        {{ title }}
      </component>
      <EField
        v-model="answer"
        name="answer"
        class="feedback-form__radios"
        data-test-id="feedback-form-answer"
        type="radio"
        required
        :options="options"
      />
      <EField
        v-if="subjects"
        v-model="subject"
        name="subject"
        class="feedback-form-select"
        data-test-id="feedback-form-subject"
        type="select"
        :force-stacked="true"
        :placeholder="subjectsPlaceholder"
        :options="subjects"
        required
      />
      <!-- Snowplow form tracker works for fields which exist in DOM when it is called -->
      <!-- Using v-show instead of v-if to handle tracking dynamic field -->
      <transition name="fade">
        <EField
          v-show="messageShown"
          v-model="message"
          name="message"
          type="textarea"
          class="feedback-form__textarea"
          data-test-id="feedback-form-message"
          maxlength="500"
          autoscale
          size="l"
          :force-stacked="true"
          :error="error"
          :placeholder="textareaPlaceholder"
        />
      </transition>
      <div class="feedback-form__actions">
        <ELoadingButton
          type="submit"
          class="feedback-form__submit"
          data-test-id="feedback-form-submit"
          variant="solid-green"
          size="l"
          :loading="sending"
          :disabled="!validity.valid || sending"
        >
          {{ submitText }}
        </ELoadingButton>
      </div>
    </form>
    <div v-else class="feedback-form__success" data-test-id="feedback-form-success">
      <slot name="success">
        <EIcon
          icon="check-circle"
          size="l"
          class="feedback-form__success-icon"
          data-test-id="feedback-form-success-icon"
        />
        <h2 class="feedback-form__title" data-test-id="feedback-form-success-title">
          {{ successTitle }}
        </h2>
        <p class="feedback-form__success-message">
          {{ successMessage }}
        </p>
      </slot>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { getBreakpoint } from '~~/common/js/client/screen';
import { sendEvent } from '~~/common/js/client/analytics';
import ecosiaDoNotTrack from '~~/common/js/client/do-not-track';
import '~~/common/vue/directives/validity';
import EField from '~~/common/vue/components/field';
import ELoadingButton from '~~/common/vue/components/loading-button';
import EIcon from '~~/common/vue/components/icon';

export const createData = () => ({
  message: '',
  answer: null,
  subject: null,
  error: '',
  sending: false,
  success: false,
  validity: {},
});

export default {
  name: 'EFeedbackForm',
  components: {
    EField,
    ELoadingButton,
    EIcon,
  },
  props: {
    errorMessage: {
      type: String,
      required: true,
    },
    /**
     * Array of { label: String, value: String};
     * e.g. { label: 'Option description', value: "option_key_for_analytics" }
     */
    options: {
      type: Array,
      required: true,
      validator: (options) => Array.isArray(options) &&
        options.every((option) => (
          Object.keys(option).includes('value') &&
          Object.keys(option).includes('label')
        )),
    },
    query: {
      type: String,
      default: '',
    },
    formId: {
      type: String,
      default: null,
    },
    subjectsPlaceholder: {
      type: String,
      default: '',
    },
    subjects: {
      type: Array,
      default: null,
    },
    submitText: {
      type: String,
      required: true,
    },
    successMessage: {
      type: String,
      required: true,
    },
    successTitle: {
      type: String,
      required: true,
    },
    textareaPlaceholder: {
      type: String,
      required: true,
    },
    ticketSubject: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    titleAs: {
      type: String,
      default: 'h2',
    },
    url: {
      type: String,
      default: '',
    },
    /**
     * Type of content to which the feedback is related.
     * e.g. "word_definitions_rich" or "uninstall".
     *
     */
    widgetType: {
      type: String,
      default: '',
    },
    /**
     * If enabled, the feedback will be sent not just to Zendesk,
     * but also to Snowplow via the standard `widget_feedback_event`.
     */
    snowplow: {
      type: Boolean,
      default: true,
    },
  },
  data: createData,
  computed: {
    ...mapState('user', ['bingMarketCode']),
    ...mapState('browser', {
      browser: 'name',
      browserVersion: 'version',
    }),
    messageShown() {
      let shown = !!this.answer;
      if (this.subjects) {
        shown = !!this.subject;
      }
      return shown;
    },
  },
  methods: {
    resetData() {
      const data = createData();
      Object.assign(this.$data, data);
    },
    feedbackSelfDescribingEvent(data = {}) {
      return {
        tracker: 'trackSelfDescribingEvent',
        data: {
          schema: 'iglu:org.ecosia/widget_feedback_event/jsonschema/1-0-1',
          data: {
            widget_type: data.widget_type,
            answer: data.answer,
            query: data.query,
            market: data.market,
            feedback_text: data.message,
          },
        },
        context: ['mainCookie', 'abTests'],
      };
    },
    track(selectedOption, widgetType) {
      const payload = {
        answer: selectedOption?.value ?? selectedOption,
        message: this.message,
        market: this.bingMarketCode,
        query: this.query,
        widget_type: widgetType,
      };
      if (this?.$track) {
        this.$track(this.feedbackSelfDescribingEvent(payload));
        return;
      }
      sendEvent('widget_feedback_submission', payload);
    },
    createTicket(selectedOption) {
      const { label: breakpoint } = getBreakpoint();
      const body = {
        answer: selectedOption?.label ?? selectedOption,
        breakpoint,
        browser: this.browser,
        browserVersion: this.browserVersion,
        market: this.bingMarketCode,
        message: this.message,
        query: this.query,
        subject: this.subject,
        doNotTrack: ecosiaDoNotTrack(),
      };
      const comment = { body: JSON.stringify(body, null, 2) };
      return {
        subject: `Feedback - ${this.ticketSubject}`,
        comment,
      };
    },
    async send() {
      if (this.sending) {
        return;
      }
      this.sending = true;

      const selectedOption = this.options.find(({ value }) => value === this.answer);
      const ticket = this.createTicket(selectedOption);
      const body = JSON.stringify({ ticket });
      try {
        if (this.snowplow) {
          this.track(selectedOption, this.widgetType);
        }
        // In case we want to collect data with snowplow only and not to send them to any endpoint.
        // url is null, and we should make sure it set.
        if (this.url) {
          const response = await fetch(this.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
          this.success = response.ok;
        } else {
          this.success = true;
        }

        this.sending = false;
        if (this.success) {
          this.$emit('success');
          return;
        }
        // using a generic error, instead of specific, to obfuscate internal info
        this.error = this.$t('common.header.feedback.error');
        // TODO: check why this is needed
      } catch (err) {
        this.sending = false;
        this.error = this.errorMessage;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.feedback-form {
  display: block;
  color: var(--color-text-primary);
}

.feedback-form__title {
  font-family: $family;
  font-size: $font-3l;
  font-weight: $font-weight-1l;
  margin-top: 0;
  margin-bottom: $space-l;
}

.feedback-form__textarea {
  min-height: $space-3l;
}

.feedback-form__actions {
  display: flex;
  justify-content: center;
  margin-top: $space-l;
}

.feedback-form__submit {
  width: auto;
}

.feedback-form__success {
  position: relative;
  text-align: center;
  overflow: auto;
  color: var(--color-text-primary);
}

.feedback-form__success-icon {
  margin: 0 0 $space-m;
  font-family: $family;
  font-size: $font-l;
  height: $space-4l;
  width: $space-4l;
  stroke-width: 5px;
  color: var(--color-brand-primary);
}

.feedback-form__success-message {
  font-family: $family;
  font-size: $font-l;
  margin: $space-s 0 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity $timing-1s $easing;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
